import type { PlanTier } from './plans.js';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * AJANIN KOD ÇALIŞTIRMASI, DOSYALARI VE BECERİLERİ — sözleşme (Faz 9 X1, 1 Eki 2026)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Ürün sahibi: "Claude'da olan tüm özellikler eklensin, hiçbir eksik kalmasın."
 * Bu dosya dört yeteneğin İKİ TARAFIN (API + web) ortak sözleşmesi:
 *
 *   · KOD ÇALIŞTIRMA — Anthropic'in sunucu tarafı sandbox'ı
 *     (`code_execution_20260521`, Python + bash, internet YOK). Hesap,
 *     istatistik, büyük tablo, grafik. Konteyner sohbet boyunca yeniden
 *     kullanılıyor (süresi dolunca yenisi).
 *   · DOSYALAR (Files API) — kullanıcının eki (görsel/PDF/veri tablosu) bir
 *     kez yükleniyor, `file_id` sohbet boyunca yeniden kullanılıyor;
 *     konteynere `container_upload` ile veriliyor. 🔴 Müşteri listesi CSV'si
 *     (şifreli kasa) ve lead/yorum verisi konteynere ve Files API'ye ASLA
 *     gitmez.
 *   · BECERİLER (Skills) — Anthropic'in hazır `xlsx` / `pptx` / `docx` /
 *     `pdf` becerileri: AI müşteriye Excel raporu, sunum, Word/PDF belge
 *     üretebiliyor. Üretilen dosya konteynerden alınıp R2'ye yazılıyor ve
 *     sohbette `file` kartı olarak çıkıyor (kart yalnız kimlik + meta taşır;
 *     indirme adresi her çizimde TAZE, rol denetimli canlı uçtan).
 *   · PROGRAMATİK ARAÇ ÇAĞIRMA — AI, YALNIZ OKUMA araçlarımızı kodun içinden
 *     çağırabiliyor (ör. 50 kampanyayı dolaşıp tek özet). Yazma araçları
 *     koddan ASLA çağrılamaz; kod içi çağrıda kart çizilmez.
 */

/** Sağlayıcının kod çalıştırma aracı — SDK 0.116.0 `BetaCodeExecutionTool20260521`. */
export const AGENT_CODE_TOOL_TYPE = 'code_execution_20260521' as const;

/** Anthropic'in hazır belge becerileri (`container.skills`, tür `anthropic`). */
export const AGENT_DOC_SKILLS = ['xlsx', 'pptx', 'docx', 'pdf'] as const;
export type AgentDocSkill = (typeof AGENT_DOC_SKILLS)[number];

/**
 * ÇALIŞMA ALANI BAŞINA GÜNLÜK KOD TAVANI — plan başına.
 *
 *   · `runs` — sağlayıcının saydığı kod çalıştırma isteği
 *     (`usage.server_tool_use.code_execution_requests`; sayı gelmezse kod
 *     bloğu taşıyan yanıt başına bir).
 *   · `containers` — açılan YENİ konteyner (sohbet başına bir tane yeniden
 *     kullanılıyor; yenisi süresi dolunca ya da yeni sohbette).
 *
 * Fiyat (Anthropic belgesi, 1 Eki 2026): istekte web araması/okuması varsa
 * kod çalıştırma EK ÜCRETSİZ; yoksa istek başına en az 5 dk, kuruluşa ayda
 * 1.550 saat bedava, üstü konteyner başına saati $0,05. Tavan bir döngüye
 * giren modelin faturayı büyütmemesi için. Gün çalışma alanının saat
 * diliminde dönüyor (Redis). Bugün bütün planlarda aynı — ürün kararı
 * gelince yalnız bu tablo değişir; okuyan tek yer `AgentCodeToolsService`.
 */
export const AGENT_CODE_DAILY_LIMIT: Record<PlanTier, { runs: number; containers: number }> = {
  TRIAL: { runs: 200, containers: 40 },
  STARTER: { runs: 200, containers: 40 },
  GROWTH: { runs: 200, containers: 40 },
  PRO: { runs: 200, containers: 40 },
  ENTERPRISE: { runs: 200, containers: 40 },
};

/** Aboneliği okunamayan çalışma alanının tavanı — sınırsız varsayılmıyor. */
export const AGENT_CODE_DAILY_FALLBACK = { runs: 40, containers: 10 } as const;

export const AGENT_CODE_LIMITS = {
  /**
   * Bir turda KODUN İÇİNDEN yapılabilecek okuma çağrısı. Doğrudan araç
   * tavanından (`AGENT_LIMITS.maxToolCallsPerTurn`) AYRI: kod 50 kampanyayı
   * dolaşırken o tavanı tek başına yerdi. Aşan çağrı yürütülmüyor; koda
   * "tavan doldu" hatası dönüyor ve kod elindekiyle bitiriyor.
   */
  maxCodeToolCallsPerTurn: 120,
  /**
   * Koddan gelen çağrılar aynı anda en fazla bu kadar yürütülüyor. Sağlayıcı
   * bekleyen çağrıya ~4 dk tanıyor; 30 okumayı tek tek sıralamak bunu aşabilir.
   */
  codeToolConcurrency: 4,
  /** Tek bir üretilen dosyanın R2'ye alınan en büyük boyutu (bayt). */
  outputFileMaxBytes: 50 * 1024 * 1024,
  /** Bir yanıtta alınan en fazla dosya — fazlası kayda düşer, kart çıkmaz. */
  outputFilesPerResponse: 10,
  /**
   * Files API'ye yüklenen kullanıcı ekinin son kullanımdan sonraki ömrü
   * (gün). Süre dolunca sağlayıcıdan siliniyor; o ekin sohbetteki satırı bir
   * sonraki istekte eski yola (base64 / tek satır) düşüyor.
   */
  inputFileRetentionDays: 7,
} as const;

/** Dosya kartının simge ailesi — uzantı/MIME'dan türetiliyor (ekran çevirir). */
export type AgentFileKind =
  | 'spreadsheet'
  | 'presentation'
  | 'document'
  | 'pdf'
  | 'image'
  | 'csv'
  | 'text'
  | 'archive'
  | 'other';

const EXT_KIND: Readonly<Record<string, AgentFileKind>> = {
  xlsx: 'spreadsheet',
  xlsm: 'spreadsheet',
  xls: 'spreadsheet',
  ods: 'spreadsheet',
  pptx: 'presentation',
  ppt: 'presentation',
  odp: 'presentation',
  docx: 'document',
  doc: 'document',
  odt: 'document',
  rtf: 'document',
  pdf: 'pdf',
  png: 'image',
  jpg: 'image',
  jpeg: 'image',
  webp: 'image',
  gif: 'image',
  svg: 'image',
  csv: 'csv',
  tsv: 'csv',
  txt: 'text',
  md: 'text',
  json: 'text',
  xml: 'text',
  html: 'text',
  zip: 'archive',
  gz: 'archive',
  tar: 'archive',
};

/** Ad uzantısı (küçük harf, noktasız) — yoksa boş dize. */
export function agentFileExtension(name: string): string {
  const match = /\.([a-z0-9]{1,8})$/i.exec(name.trim());
  return match ? (match[1] as string).toLowerCase() : '';
}

export function agentFileKindOf(name: string, mime: string | null | undefined): AgentFileKind {
  const byExt = EXT_KIND[agentFileExtension(name)];
  if (byExt) return byExt;
  const type = (mime ?? '').toLowerCase();
  if (type.startsWith('image/')) return 'image';
  if (type === 'application/pdf') return 'pdf';
  if (type.includes('spreadsheet') || type.includes('excel')) return 'spreadsheet';
  if (type.includes('presentation') || type.includes('powerpoint')) return 'presentation';
  if (type.includes('wordprocessing') || type.includes('msword')) return 'document';
  if (type === 'text/csv') return 'csv';
  if (type.startsWith('text/')) return 'text';
  return 'other';
}

/**
 * Ekranda ÖNİZLENEBİLEN tür — yalnız raster görsel (`<img>`). SVG bilerek
 * dışarıda: betik taşıyabilir; indirme `attachment` ile iner, çizilmez.
 */
export function agentFilePreviewable(name: string, mime: string | null | undefined): boolean {
  const ext = agentFileExtension(name);
  if (['png', 'jpg', 'jpeg', 'webp', 'gif'].includes(ext)) return true;
  const type = (mime ?? '').toLowerCase();
  return type === 'image/png' || type === 'image/jpeg' || type === 'image/webp' || type === 'image/gif';
}

/** Dosyayı üreten beceri — uzantıdan tahmin (kart rozeti; bilinmiyorsa `null`). */
export function agentDocSkillOf(name: string): AgentDocSkill | null {
  const ext = agentFileExtension(name);
  if (ext === 'xlsx' || ext === 'xlsm') return 'xlsx';
  if (ext === 'pptx') return 'pptx';
  if (ext === 'docx') return 'docx';
  if (ext === 'pdf') return 'pdf';
  return null;
}

/**
 * CSV BAŞLIĞI MÜŞTERİ LİSTESİ Mİ (Faz 9 X1) — tarayıcı CSV'yi nereye
 * göndereceğine buna bakarak karar veriyor: kişi kolonu (e-posta, telefon,
 * ad soyad…) varsa ŞİFRELİ KASAYA (müşteri listesi), yoksa depoya (veri
 * tablosu → kod çalıştırma). Sunucu konteynere vermeden önce dosyanın
 * kendisini yeniden tarıyor (`data-file-scan.ts`) — bu yalnız yönlendirme.
 *
 * `name` / `ad` tek başına sayılmıyor: reklam dışa aktarımlarında kampanya/
 * reklam adı ("Name", "Ad").
 */
const CUSTOMER_COLUMN_KEYS: ReadonlySet<string> = new Set([
  'email',
  'emailaddress',
  'workemail',
  'businessemail',
  'eposta',
  'epostaadresi',
  'mail',
  'phone',
  'phonenumber',
  'mobile',
  'mobilephone',
  'mobilenumber',
  'telephone',
  'tel',
  'telefon',
  'telefonnumarasi',
  'gsm',
  'firstname',
  'givenname',
  'lastname',
  'familyname',
  'surname',
  'soyad',
  'soyadi',
  'fullname',
  'adsoyad',
  'adisoyadi',
  'isim',
  'isimsoyisim',
]);

function columnKey(header: string): string {
  return header
    .toLocaleLowerCase('tr')
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/ı/g, 'i')
    .replace(/[^a-z0-9]/g, '');
}

export function looksLikeCustomerListHeader(headerLine: string): boolean {
  const line = headerLine.charCodeAt(0) === 0xfeff ? headerLine.slice(1) : headerLine;
  const delimiter = [';', '\t', ','].reduce((best, candidate) =>
    line.split(candidate).length > line.split(best).length ? candidate : best,
  );
  return line
    .split(delimiter)
    .map((cell) => columnKey(cell.replace(/^"|"$/g, '')))
    .some((key) => CUSTOMER_COLUMN_KEYS.has(key));
}
